import React from 'react';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { mittagsmenueData } from '../data/menuData';
import { Badge } from '../components/ui/badge';

const MittagsmenueSection = () => {
  const { language, t } = useLanguage();

  const days = ['montag', 'dienstag', 'mittwoch', 'donnerstag', 'freitag'];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="mittagessen" 
      className="py-16 md:py-24 bg-hanse-dark paper-texture relative"
      data-testid="mittagessen-section"
    >
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <Badge className="bg-hanse-gold text-hanse-dark mb-4">
            {t('mittagessen.badge')}
          </Badge>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('mittagessen.title')}
          </h2>
          <p className="text-white/70 text-base md:text-lg flex items-center justify-center gap-2">
            <Clock className="w-5 h-5 text-hanse-gold" />
            {t('mittagessen.subtitle')}
          </p>
        </motion.div>

        {/* Menu Cards - 5 days in a row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {days.map((day, index) => {
            const dayData = mittagsmenueData[day];
            return (
              <motion.div
                key={day}
                id={`menu-${day}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-hanse-gold/50 transition-all group"
                data-testid={`lunch-card-${day}`}
              >
                {/* Day Name */}
                <h3 className="font-heading text-lg font-bold text-hanse-gold mb-3 text-center group-hover:text-hanse-gold/90">
                  {language === 'de' ? dayData.day : dayData.dayEn}
                </h3>

                {/* Menu 1 */}
                <div className="mb-3 pb-3 border-b border-white/10">
                  <span className="text-xs text-white/50 uppercase tracking-wider">
                    {t('common.menu1')}
                  </span>
                  <p className="text-white font-medium text-sm mt-1 leading-snug">
                    {language === 'de' ? dayData.menu1.name : dayData.menu1.nameEn}
                  </p>
                </div>

                {/* Menu 2 */}
                <div>
                  <span className="text-xs text-white/50 uppercase tracking-wider">
                    {t('common.menu2')}
                  </span>
                  <p className="text-white font-medium text-sm mt-1 leading-snug">
                    {language === 'de' ? dayData.menu2.name : dayData.menu2.nameEn}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Drinks Note & CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-white/60 text-sm mb-4">
            🥤 {t('mittagessen.drinksNote')}
          </p>
          <button
            onClick={() => scrollToSection('#speisekarte')}
            className="inline-flex items-center gap-2 bg-hanse-red hover:bg-hanse-red/90 text-white px-6 py-3 rounded-full font-semibold transition-all hover:scale-105"
            data-testid="lunch-view-menu-btn"
          >
            📋 {t('mittagessen.viewMenu')}
            <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default MittagsmenueSection;
